import { useState } from "react";
import { motion } from "framer-motion";
import { STUDIO } from "@/constants/testIds";
import ClientListModal from "@/components/site/ClientListModal";

const EASE = [0.76, 0, 0.24, 1];

const RevealHeading = ({ children, className = "" }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      whileInView={{ y: "0%" }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 1.0, ease: EASE }}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

const BLOCKS = [
  {
    slug: "who",
    kicker: "Who We Are",
    lede: "Brand stewards, storytellers, and developers for the built environment.",
    body: [
      "HANKDAVID operates at the intersection of brand creation and strategic partnership. We concept, develop, and execute our own portfolio of hospitality brands and scalable intellectual property while simultaneously serving as creative partners to select clients who share our commitment to narrative-driven design.",
      "Our in-house studio is the engine behind everything we do. It supports key strategic partnerships with clients while simultaneously building HANKDAVID's own slate of hospitality experiences and developments. This dual approach means we understand hospitality from both sides: as creators building our own brands, and as collaborators helping others realize theirs.",
    ],
  },
  {
    slug: "what",
    kicker: "What We Do",
    lede: "From concept to completion, we bring stories to life.",
    body: [
      "Whether your property needs a thoughtful refresh, a full brand transformation, or a concept built from the ground up, we bring your story to life. We seamlessly integrate concept, real estate, design, and strategy to transform unique sites into immersive environments where elevated design and thoughtful detailing harmonize with the natural landscape.",
      "From crafting brand guidelines to creating immersive experiences, our goal is to position your project for both experiential impact and financial success. With HANKDAVID, your brand becomes a powerful, cohesive narrative that resonates and endures.",
    ],
  },
  {
    slug: "how",
    kicker: "How We Work",
    lede: "Hospitality as world-building.",
    body: [
      "Our team brings over two decades of in-depth experience in all areas of design, from strategic planning and conceptual development to FF&E procurement and on-site execution. We support your project from start to finish, ensuring every detail aligns with the overarching narrative.",
      "We don't simply layer design onto a project; we unearth the inherent story of place, translate it into experience, and craft environments that feel both inevitable and extraordinary. Every material choice, spatial flow, and sensory detail serves the larger story.",
    ],
  },
];

export default function Studio() {
  const [listOpen, setListOpen] = useState(false);
  return (
    <section
      id="studio"
      data-testid={STUDIO.root}
      className="relative bg-[#0a0a0b] text-white border-t border-white/[0.06]"
    >
      {/* Section header */}
      <div className="px-6 md:px-10 pt-32 md:pt-48 pb-16 md:pb-24">
        <div className="flex items-center gap-4 mb-10">
          <span className="mono-label">§ HQ</span>
          <div className="hairline flex-1 max-w-32" />
          <span className="mono-label">Detroit</span>
        </div>
        <h2 className="font-display !normal-case text-[10vw] md:text-[7vw] lg:text-[6vw] leading-[0.94] tracking-[-0.03em] max-w-[18ch]">
          <RevealHeading>Our Studio</RevealHeading>
        </h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="mt-10 md:mt-14 max-w-2xl text-white/60 text-sm md:text-lg leading-relaxed"
        >
          An in-house studio building HANKDAVID&rsquo;s slate of hospitality experiences,
          partnering with clients who share our commitment to narrative-driven design.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}
          className="mt-8 md:mt-10"
        >
          <button
            type="button"
            onClick={() => setListOpen(true)}
            data-testid="studio-client-list-btn"
            data-cursor="→"
            className="group inline-flex items-center gap-3 mono-label text-white border-b border-white/40 pb-2 hover:border-white transition-colors"
          >
            <span className="text-left">
              Client list available{" "}<br className="md:hidden" />upon request
            </span>
            <span
              aria-hidden="true"
              className="inline-block transition-transform group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </button>
        </motion.div>
      </div>

      <ClientListModal open={listOpen} onClose={() => setListOpen(false)} />

      <div className="hairline" />

      {/* Numbered content blocks */}
      <div className="divide-y divide-white/[0.06]">
        {BLOCKS.map((b) => (
          <motion.article
            key={b.slug}
            data-testid={STUDIO.block(b.slug)}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
            className="grid grid-cols-12 gap-6 md:gap-10 px-6 md:px-10 py-16 md:py-28"
          >
            {/* Left, kicker */}
            <div className="col-span-12 md:col-span-4 lg:col-span-3">
              <div className="sticky top-32">
                <div className="font-display !normal-case text-white/80 text-lg md:text-xl tracking-tight">{b.kicker}</div>
              </div>
            </div>

            {/* Right, lede + body */}
            <div className="col-span-12 md:col-span-8 lg:col-span-8 lg:col-start-5">
              <h3 className="font-display !normal-case text-4xl md:text-4xl lg:text-7xl leading-[1.02] tracking-[-0.02em] max-w-[20ch]">
                {b.lede}
              </h3>
              <div className="mt-10 md:mt-14 space-y-6 max-w-2xl">
                {b.body.map((para) => (
                  <p
                    key={para}
                    className="text-white/70 text-sm md:text-xl leading-[1.7]"
                  >
                    {para}
                  </p>
                ))}
              </div>

              {/* Small footer meta */}
              <div className="mt-12 md:mt-16 flex items-center gap-4">
                <div className="hairline w-16" />
              </div>
            </div>
          </motion.article>
        ))}
      </div>

    </section>
  );
}
